import * as api from '@/requests/api/post';
import {
  parsedErrorNeedsMotivation,
  requestUserMotivation,
} from '@/lib/api/motivation';
import type { BaseInstance } from '@pigello/pigello-matrix';
import type { RequestData } from '../types';
import { patch } from './patch';

export { parsedErrorNeedsMotivation, requestUserMotivation };

type Next = () => Promise<{
  data: BaseInstance | Array<BaseInstance> | Record<string, unknown>;
}>;

const isObject = (obj: unknown): obj is Record<string, unknown> => {
  return typeof obj === 'object' && obj !== null;
};

/**
 * Builds a retry for the given request with motivation merged into the body.
 * Call once per request so each chunk keeps its own payload (e.g. bulk POST batches).
 */
export const buildRetryWithMotivation = (
  requestData: RequestData,
  motivation: string
): Next | undefined => {
  let parsedBody: unknown;
  try {
    const bodyString =
      typeof requestData.options?.body === 'string'
        ? requestData.options.body
        : JSON.stringify(requestData.options?.body ?? {});
    parsedBody = JSON.parse(bodyString);
  } catch {
    return undefined;
  }
  if (!Array.isArray(parsedBody) && !isObject(parsedBody)) {
    return undefined;
  }
  if (Object.keys(parsedBody as object).length === 0) {
    return undefined;
  }

  let newBodyData: unknown;

  if (Array.isArray(parsedBody)) {
    newBodyData = parsedBody.map((inst) => ({
      ...inst,
      mt_motivation: motivation,
    }));
  } else {
    newBodyData = {
      ...parsedBody,
      mt_motivation: motivation,
    };
  }

  const newRequestData: RequestData = {
    ...requestData,
    options: {
      ...requestData.options,
      body: JSON.stringify(newBodyData),
    },
  };

  switch (requestData.type) {
    case 'POST':
      return async () =>
        await api.post({ url: '', body: {}, _requestData: newRequestData });
    case 'PATCH':
      return async () =>
        await patch({ url: '', body: {}, _requestData: newRequestData });
    default:
      throw Error('un impletented');
  }
};

export const requestMotivationAndRetryPost = async (
  parsedError: unknown,
  requestData: RequestData
): Promise<{
  next: Next | undefined;
  data:
    | Awaited<ReturnType<Next>>['data']
    | Array<Awaited<ReturnType<Next>>['data']>;
}> => {
  type ResponseData = Awaited<ReturnType<Next>>['data'];

  try {
    const bodyString =
      typeof requestData.options?.body === 'string'
        ? requestData.options.body
        : JSON.stringify(requestData.options?.body ?? {});
    const parsedBody = JSON.parse(bodyString);
    if (Object.keys(parsedBody).length === 0) {
      return { next: undefined, data: parsedError as ResponseData };
    }

    if (!Array.isArray(parsedError) && !isObject(parsedError)) {
      return { next: undefined, data: parsedError as ResponseData };
    }

    if (!parsedErrorNeedsMotivation(parsedError)) {
      return { next: undefined, data: parsedError as ResponseData };
    }

    const motivation = await requestUserMotivation();
    if (motivation === null) {
      return { next: undefined, data: parsedError as ResponseData };
    }

    const next = buildRetryWithMotivation(requestData, motivation);
    return { next, data: parsedError as ResponseData };
  } catch (err) {
    console.warn('exited with error', err);
    return { next: undefined, data: parsedError as ResponseData };
  }
};

export const handleMotivation = async (
  res: Response,
  requestData?: RequestData
): Promise<{
  next: Next | undefined;
  data:
    | Awaited<ReturnType<Next>>['data']
    | Array<Awaited<ReturnType<Next>>['data']>;
}> => {
  const parsedError = await res.json();

  if (!requestData) {
    return { next: undefined, data: parsedError };
  }

  return requestMotivationAndRetryPost(parsedError, requestData);
};
